import React, { useEffect, useRef, useState } from "react";
import { ReactComponent as ArrowIcon } from "../../../../assets/Icons/arrownext.svg";
import ShowCard from "../../../../Components/ShowCard/GaiaCard";
import { Swiper, SwiperSlide } from "swiper/react";
import { Navigation } from "swiper";
import { rowItemCount } from "../../../../utils/utils";
import { Link, useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { useWindowWidth } from "@react-hook/window-size";
import MagazineCard from "../../../../Components/MagazineCard/MagazineCard";
import ShortsCard from "../../../Shorts/ShortsCard";
import { navigateToCategory } from "../../../../utils/utils";
import Translate, { useTranslateText } from "../../../../Components/Multilanguage/Translate";
import ConfirmationPopup from "../../../../Components/ConfirmationPopup/ConfirmationPopup";
import Util from "../../../../Components/ShowCard/util";

const GaiaCategories = ({ data, onRemoveItem, fromFavoritesScreen = false }) => {
  const windowSize = useWindowWidth();
  const projectInfo = useSelector((state) => state?.projectInfo?.value);
  const pubid = projectInfo?.projectConfig?.pubid || projectInfo?.pubid;
  const isFlsTv = pubid == 50183 || Number(pubid) === 50183;
  const isYowTv = pubid == 50187 || Number(pubid) === 50187;
  const isFavoritesYowTv = (isYowTv || pubid == 50187) && fromFavoritesScreen;
  const enableCustomRowCount = true;
  const isContinueWatchingCategory =
    data?.type === "CONTINUE_WATCHING" ||
    data?.category_name === "Continue Watching" ||
    data?.title === "Continue Watching" ||
    data?.name === "Continue Watching";
  const isContinueWatchingYowTv = (isContinueWatchingCategory && (isYowTv || pubid == 50187));
  const isTopTrendingYowTv = (data?.type === "TOP_TRENDING" && (isYowTv || pubid == 50187));
  const isYowTv4CardRow = isContinueWatchingYowTv || isTopTrendingYowTv;
  const visibleItemsCount = isYowTv4CardRow
    ? 4
    : isFavoritesYowTv
      ? 6
      : ((enableCustomRowCount && data?.visible_items_count) ? Math.ceil(Number(data.visible_items_count)) : null);

  const thumbnailOrientation = (data?.type === "TOP_TRENDING" || isContinueWatchingCategory) 
    ? "LANDSCAPE" 
    : (data?.thumbnail_orientation ? data?.thumbnail_orientation : projectInfo?.projectConfig?.config?.THUMBNAIL_ORIENTATION);

  // For YowTV (pubid 50187): Top Trending & Continue Watching rows always show 4 thumbnails;
  // MyList (favorites) rows always default to 6 thumbnails;
  // all other category rows default to visible_items_count (or 7 for PORTRAIT / 5 for LANDSCAPE).
  const defaultYowTvCount = isYowTv4CardRow
    ? 4
    : isFavoritesYowTv
      ? 6
      : (visibleItemsCount || (thumbnailOrientation === "PORTRAIT" ? 7 : 5));

  const initialConfigCount = isYowTv
    ? defaultYowTvCount
    : (visibleItemsCount ?? projectInfo?.projectConfig?.config?.WEBSITE_ROW_ITEM_COUNT ?? 5);
  const initialRowClass = rowItemCount(Number(initialConfigCount)) || (isYowTv ? `rowCount${defaultYowTvCount}` : "rowCount4");
  const initialRowCountNumber = parseInt(initialRowClass.replace(/\D/g, ""), 10) || defaultYowTvCount;

  const [activeIndex, setActiveIndex] = useState(0);
  const [currentSlideIndex, setCurrentSlideIndex] = useState(0);
  const [isNavigating, setIsNavigating] = useState(false);
  const [isControlsHovered, setIsControlsHovered] = useState(false);
  const navigationTimeoutRef = useRef(null);
  const [rowItemsClass, setRowItemsClass] = useState(initialRowClass);
  const [rowItemCountNumber, setRowItemCountNumber] = useState(initialRowCountNumber);
  const effectiveCount = isYowTv4CardRow ? 4 : ((enableCustomRowCount ? visibleItemsCount : null) ?? projectInfo?.projectConfig?.config?.WEBSITE_ROW_ITEM_COUNT);
  const isRTL = useSelector((state) => state?.rtl?.isRTL);
  const portraitFallback = projectInfo?.projectConfig?.config?.WEBSITE_ROW_ITEM_COUNT ?? 7;
  const landscapeFallback = projectInfo?.projectConfig?.config?.WEBSITE_ROW_ITEM_COUNT ?? 5;
  const navigate = useNavigate();
  const swiperRef = useRef(null);
  const prevRef = useRef(null);
  const nextRef = useRef(null);

  const count = data?.shows?.length;
  const [displayedData, setDisplayedData] = useState(data?.shows || []);
  const isSingleCard = data?.shows?.length === 1;

  const [showConfirm, setShowConfirm] = useState(false);
  const playlistId = data?.playlist_id;
  const accessToken = useSelector((state) => state?.accessToken?.value);
  const translateText = useTranslateText();
  const { deletePlaylist } = Util();

  const appInfo = {
    projectDetails: projectInfo,
    accessToken: accessToken,
  };

  useEffect(() => {
    setDisplayedData(data?.shows || []);
    if (swiperRef.current) {
      setTimeout(() => {
        if (swiperRef.current && !swiperRef.current.destroyed) {
          swiperRef.current.update();
          setIsBeginning(swiperRef.current.isBeginning);
          setIsEnd(swiperRef.current.isEnd);
        }
      }, 50);
    }
  }, [data?.shows]);

  const handleDeletePlaylist = async () => {
    try {
      await deletePlaylist(appInfo, [playlistId]);
      setShowConfirm(false);
    } catch (err) {
      setShowConfirm(false);
    }
  };

  const [isBeginning, setIsBeginning] = useState(true);
  const [isEnd, setIsEnd] = useState(false);

  const getVisibleCount = () => {
    if (isYowTv4CardRow) return 4;
    if (isFavoritesYowTv) {
      if (windowSize >= 980) return 6;
      if (windowSize >= 768) return 4;
      return 2;
    }
    if (swiperRef.current?.params?.slidesPerView && typeof swiperRef.current.params.slidesPerView === "number") {
      return Math.floor(swiperRef.current.params.slidesPerView);
    }
    if (isYowTv) {
      if (visibleItemsCount) {
        if (windowSize >= 1200) return visibleItemsCount;
        if (windowSize >= 980) return Math.min(visibleItemsCount, 6);
        if (windowSize >= 768) return 4;
        return 2;
      }
      if (windowSize >= 1200) return thumbnailOrientation === "PORTRAIT" ? 7 : 5;
      if (windowSize >= 980) return thumbnailOrientation === "PORTRAIT" ? 6 : 4;
      if (windowSize >= 768) return 4;
      return 2;
    }
    if (enableCustomRowCount && data?.visible_items_count) {
      return Math.ceil(Number(data.visible_items_count));
    }
    if (windowSize >= 1200) return thumbnailOrientation === "PORTRAIT" ? 7 : 5;
    if (windowSize >= 980) return 4;
    return 2;
  };

  const [visibleSlidesCount, setVisibleSlidesCount] = useState(() => {
    if (isYowTv) return defaultYowTvCount;
    if (typeof window !== "undefined" && window.innerWidth >= 1200) {
      return thumbnailOrientation === "PORTRAIT" ? 7 : 5;
    }
    return 6;
  });

  const onSlideChange = (swiper) => {
    setIsBeginning(swiper.isBeginning);
    setIsEnd(swiper.isEnd);
    setCurrentSlideIndex(swiper.activeIndex || 0);
    if (swiper?.params?.slidesPerView && typeof swiper.params.slidesPerView === "number") {
      setVisibleSlidesCount(Math.floor(swiper.params.slidesPerView));
    }
    const lastSlideIndex = swiper.activeIndex + (swiper.params.slidesPerView - 1);

    if (lastSlideIndex >= swiper.slides.length) {
      setActiveIndex(swiper.slides.length - 1);
    } else {
      setActiveIndex(lastSlideIndex);
    }
  };

  const handleRemoveItem = (videoIdToRemove) => {
    setDisplayedData(prevData => {
      const updated = prevData.filter(item => item.video_id !== videoIdToRemove);
      if (onRemoveItem) {
        onRemoveItem(data?.id || data?.category_name, videoIdToRemove);
      }
      return updated;
    });
  };
  const handleSwiperInit = (swiperInstance) => {
    swiperRef.current = swiperInstance;
    swiperInstance.update();
    setIsBeginning(swiperInstance.isBeginning);
    setIsEnd(swiperInstance.isEnd);
    setCurrentSlideIndex(swiperInstance.activeIndex || 0);
    if (swiperInstance?.params?.slidesPerView && typeof swiperInstance.params.slidesPerView === "number") {
      setVisibleSlidesCount(Math.floor(swiperInstance.params.slidesPerView));
    }
    onSlideChange(swiperInstance);
    setTimeout(() => {
      if (swiperInstance && !swiperInstance.destroyed) {
        swiperInstance.update();
        setIsBeginning(swiperInstance.isBeginning);
        setIsEnd(swiperInstance.isEnd);
      }
    }, 100);
  };

  useEffect(() => {
    return () => {
      if (navigationTimeoutRef.current) clearTimeout(navigationTimeoutRef.current);
    };
  }, []);

  const handleSlidePrev = (e) => {
    if (e) {
      if (e.preventDefault) e.preventDefault();
      if (e.stopPropagation) e.stopPropagation();
    }
    setIsNavigating(true);
    if (navigationTimeoutRef.current) clearTimeout(navigationTimeoutRef.current);
    navigationTimeoutRef.current = setTimeout(() => {
      setIsNavigating(false);
    }, 600);
    if (swiperRef.current) {
      swiperRef.current.slidePrev();
      setTimeout(() => {
        if (swiperRef.current && !swiperRef.current.destroyed) {
          setIsBeginning(swiperRef.current.isBeginning);
          setIsEnd(swiperRef.current.isEnd);
        }
      }, 50);
    }
  };

  const handleSlideNext = (e) => {
    if (e) {
      if (e.preventDefault) e.preventDefault();
      if (e.stopPropagation) e.stopPropagation();
    }
    setIsNavigating(true);
    if (navigationTimeoutRef.current) clearTimeout(navigationTimeoutRef.current);
    navigationTimeoutRef.current = setTimeout(() => {
      setIsNavigating(false);
    }, 600);
    if (swiperRef.current) {
      swiperRef.current.slideNext();
      setTimeout(() => {
        if (swiperRef.current && !swiperRef.current.destroyed) {
          setIsBeginning(swiperRef.current.isBeginning);
          setIsEnd(swiperRef.current.isEnd);
        }
      }, 50);
    }
  };
  // useEffect(() => {
  //   if(windowSize>980){
  //     if(projectInfo?.projectConfig?.config?.WEBSITE_ROW_ITEM_COUNT){
  //       setRowItemsClass(rowItemCount(projectInfo?.projectConfig?.config?.WEBSITE_ROW_ITEM_COUNT))
  //     }else {
  //       setRowItemsClass("rowCount5");
  //     }
  //   } else {
  //     setRowItemsClass("rowCount5");
  //   }
  //   setRowItemCountNumber(parseInt(rowItemsClass.replace(/\D/g, ''), 10));
  // }, [projectInfo,windowSize]);
  useEffect(() => {
    if (windowSize > 1200) {
      if (data?.type === 'SHORTS') {
        setRowItemCountNumber(5);
      } else if (isYowTv) {
        const count = isYowTv4CardRow
          ? 4
          : isFavoritesYowTv
            ? 6
            : (visibleItemsCount || (thumbnailOrientation === "PORTRAIT" ? 7 : 5));
        setRowItemCountNumber(count);
        setRowItemsClass(rowItemCount(count) || `rowCount${count}`);
      }
    }
    if (windowSize > 980) {
      if (isYowTv) {
        const count = isYowTv4CardRow
          ? 4
          : isFavoritesYowTv
            ? 6
            : (visibleItemsCount ? Math.min(visibleItemsCount, 6) : (thumbnailOrientation === "PORTRAIT" ? 6 : 4));
        setRowItemCountNumber(count);
        setRowItemsClass(rowItemCount(count) || `rowCount${count}`);
      } else {
        const configCount = isYowTv4CardRow
          ? 4
          : ((enableCustomRowCount ? visibleItemsCount : null) ?? projectInfo?.projectConfig?.config?.WEBSITE_ROW_ITEM_COUNT);
        const newClass = configCount ? rowItemCount(configCount) : "rowCount5";
        setRowItemsClass(newClass);
        setRowItemCountNumber(isYowTv4CardRow ? 4 : parseInt(newClass.replace(/\D/g, ''), 10));
      }
      if (data?.type === 'FASTCHANNELS') {
        setRowItemCountNumber(9);
      }
      if (data?.type === 'SHORTS') {
        setRowItemCountNumber(4);
      }
    } else {
      setRowItemsClass("rowCount5");
      if (windowSize <= 360) {
        if (thumbnailOrientation === "PORTRAIT") {
          setRowItemCountNumber(3);
        } else {
          setRowItemCountNumber(2);
        }
      }
      else if (windowSize > 767) {
        setRowItemCountNumber(isYowTv ? 4 : (isFavoritesYowTv ? 4 : 3));
      } else if (windowSize > 479) {
        setRowItemCountNumber(2);
      } else if (windowSize > 319) {
        setRowItemCountNumber(1);
      }
      if (data?.type === 'FASTCHANNELS') {
        if (windowSize > 767) {
          setRowItemCountNumber(4);
        } else if (windowSize > 319) {
          setRowItemCountNumber(3);
        }
      }
    }
    if (swiperRef.current?.params?.slidesPerView && typeof swiperRef.current.params.slidesPerView === "number") {
      setVisibleSlidesCount(Math.floor(swiperRef.current.params.slidesPerView));
    } else {
      setVisibleSlidesCount(getVisibleCount());
    }
  }, [projectInfo, windowSize, visibleItemsCount, isFavoritesYowTv]);

  const itemsToRender = (isRTL && count > rowItemCountNumber) ? [...displayedData].reverse() : displayedData;
  console.log(windowSize, "windowSize ");

  return (
    <div
      className={`gaiaCategoriesContainer ${isFlsTv ? "fls-tv-categories" : ""} ${isYowTv ? "yowtv-categories" : ""} ${data?.type === "TOP_TRENDING" ? "top-trending-row" : ""} pubid-${projectInfo?.projectConfig?.pubid} ${projectInfo?.projectConfig?.pubid == 50183 ? "pubid-50183" : ""} ${projectInfo?.projectConfig?.pubid == 50187 ? "pubid-50187" : ""} ${isRTL ? (
        count > rowItemCountNumber
          ? "ltr"
          : windowSize < 980
            ? "rtl"
            : "rtl"
      ) : (
        ""
      )}`}
      role="region"
      aria-label="Categories Section"
    >
      <div className="header-row">
        <div className="header">
          <h1>{data?.category_name}</h1>
          {fromFavoritesScreen && playlistId !== undefined && playlistId !== null && Number(playlistId) !== 0 && (
            <svg
              className="delete-playlist-icon"
              onClick={(e) => {
                e.stopPropagation();
                setShowConfirm(true);
              }}
              onMouseEnter={(e) => (e.currentTarget.style.stroke = "var(--button-hover, #FFC800)")}
              onMouseLeave={(e) => (e.currentTarget.style.stroke = "var(--button-color, #F6F6F6)")}
              viewBox="0 0 24 24"
              fill="none"
              aria-label="Delete playlist"
              width="22"
              height="22"
              style={{ cursor: "pointer", marginLeft: "12px", stroke: "var(--button-color, #F6F6F6)" }}
            >
              <path d="M10 12V17" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
              <path d="M14 12V17" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
              <path d="M4 7H20" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
              <path d="M6 10V18C6 19.6569 7.34315 21 9 21H15C16.6569 21 18 19.6569 18 18V10" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
              <path d="M9 5C9 3.89543 9.89543 3 11 3H13C14.1046 3 15 3.89543 15 5V7H9V5Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          )}
          {(data?.type === "CONTINUE_WATCHING" || isYowTv) && (
            <span className="yow-category-chevron" aria-hidden="true">
              <svg width="8" height="12" viewBox="0 0 8 12" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M1.5 1L6.5 6L1.5 11" stroke="#8A8A88" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </span>
          )}
        </div>
        {(data?.show_all_needed || (isYowTv && data?.shows?.length > 0)) && (
          <div className="view-all"
            onClick={() => navigateToCategory(navigate, data, thumbnailOrientation)}
            role="button"
            tabIndex={0}
            aria-label={`View all ${data?.category_name}`}
            onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') navigateToCategory(navigate, data, thumbnailOrientation); }}
          >
            {isYowTv ? (
              <span className="yow-view-all-text" aria-label="View all">
                <svg width="55" height="15" viewBox="0 0 55 15" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M4.208 11.5679L0.16 0.59187H2.096L4.816 8.25587C4.86933 8.38387 4.92267 8.5332 4.976 8.70387C5.02933 8.87454 5.08267 9.03987 5.136 9.19987C5.18933 9.3492 5.23733 9.48254 5.28 9.59987H5.36C5.40267 9.48254 5.45067 9.3492 5.504 9.19987C5.55733 9.05054 5.61067 8.89587 5.664 8.73587C5.71733 8.57587 5.77067 8.4212 5.824 8.27187L8.512 0.59187H10.384L6.32 11.5679H4.208ZM11.6189 1.69587V-0.0001297H13.2829V1.69587H11.6189ZM11.6189 11.5679V3.15187H13.2829V11.5679H11.6189ZM18.9194 11.7599C18.0554 11.7599 17.33 11.6052 16.7434 11.2959C16.1674 10.9759 15.73 10.4905 15.4314 9.83987C15.1327 9.1892 14.9834 8.36254 14.9834 7.35987C14.9834 6.34654 15.1327 5.51987 15.4314 4.87987C15.73 4.2292 16.1727 3.7492 16.7594 3.43987C17.346 3.11987 18.0767 2.95987 18.9514 2.95987C19.7514 2.95987 20.418 3.11454 20.9514 3.42387C21.4954 3.72254 21.906 4.1812 22.1834 4.79987C22.4607 5.41854 22.5994 6.1972 22.5994 7.13587V7.77587H16.6954C16.7167 8.3732 16.8074 8.87454 16.9674 9.27987C17.1274 9.67454 17.3674 9.96787 17.6874 10.1599C18.018 10.3412 18.434 10.4319 18.9354 10.4319C19.2447 10.4319 19.522 10.3945 19.7674 10.3199C20.0127 10.2345 20.2207 10.1172 20.3914 9.96787C20.562 9.80787 20.6954 9.6212 20.7914 9.40787C20.8874 9.18387 20.9354 8.9332 20.9354 8.65587H22.5674C22.5674 9.1572 22.482 9.59987 22.3114 9.98387C22.1407 10.3679 21.89 10.6932 21.5594 10.9599C21.2394 11.2159 20.8554 11.4132 20.4074 11.5519C19.9594 11.6905 19.4634 11.7599 18.9194 11.7599ZM16.7274 6.55987H20.8554C20.8554 6.14387 20.8074 5.7972 20.7114 5.51987C20.626 5.23187 20.498 4.9972 20.3274 4.81587C20.1567 4.63454 19.954 4.50654 19.7194 4.43187C19.4847 4.34654 19.218 4.30387 18.9194 4.30387C18.4714 4.30387 18.0874 4.38387 17.7674 4.54387C17.458 4.70387 17.218 4.95454 17.0474 5.29587C16.8767 5.62654 16.77 6.04787 16.7274 6.55987ZM25.8588 11.5679L23.3308 3.15187H25.0588L26.2908 7.75987C26.3548 7.98387 26.4081 8.21854 26.4508 8.46387C26.5041 8.7092 26.5521 8.92787 26.5948 9.11987C26.6374 9.31187 26.6694 9.4452 26.6908 9.51987H26.7868C26.8294 9.32787 26.8774 9.1092 26.9308 8.86387C26.9841 8.61854 27.0321 8.39454 27.0748 8.19187C27.1174 7.97854 27.1494 7.81854 27.1708 7.71187L28.2908 3.15187H30.0828L31.2348 7.72787C31.2774 7.89854 31.3201 8.09587 31.3628 8.31987C31.4161 8.5332 31.4641 8.74654 31.5068 8.95987C31.5601 9.1732 31.5974 9.35987 31.6188 9.51987H31.7148C31.7468 9.37054 31.7841 9.19454 31.8268 8.99187C31.8801 8.77854 31.9334 8.5652 31.9868 8.35187C32.0401 8.13854 32.0881 7.9412 32.1308 7.75987L33.3468 3.15187H34.9308L32.4188 11.5679H30.6908L29.6668 7.37587C29.6241 7.1732 29.5708 6.93854 29.5068 6.67187C29.4428 6.4052 29.3841 6.13854 29.3308 5.87187C29.2774 5.6052 29.2348 5.37054 29.2028 5.16787H29.1068C29.0854 5.2852 29.0534 5.45587 29.0108 5.67987C28.9788 5.90387 28.9308 6.15987 28.8668 6.44787C28.8028 6.73587 28.7281 7.0452 28.6428 7.37587L27.5868 11.5679H25.8588ZM41.5438 11.7599C41.2771 11.7599 40.9891 11.7279 40.6798 11.6639C40.3704 11.5999 40.0771 11.4825 39.7998 11.3119C39.5331 11.1412 39.3144 10.9065 39.1438 10.6079C38.9731 10.2985 38.8878 9.89854 38.8878 9.40787C38.8878 8.83187 39.0211 8.3572 39.2878 7.98387C39.5544 7.61054 39.9278 7.3172 40.4078 7.10387C40.8878 6.89054 41.4584 6.7412 42.1198 6.65587C42.7918 6.57054 43.5224 6.52787 44.3118 6.52787V5.69587C44.3118 5.41854 44.2638 5.1732 44.1678 4.95987C44.0824 4.74654 43.9171 4.5812 43.6718 4.46387C43.4371 4.34654 43.0958 4.28787 42.6478 4.28787C42.1998 4.28787 41.8478 4.34654 41.5918 4.46387C41.3464 4.57054 41.1704 4.7092 41.0638 4.87987C40.9678 5.03987 40.9198 5.21587 40.9198 5.40787V5.64787H39.3198C39.3091 5.59454 39.3038 5.5412 39.3038 5.48787C39.3038 5.43454 39.3038 5.37054 39.3038 5.29587C39.3038 4.79454 44.4424 4.3732 39.7198 4.03187C40.0078 3.67987 40.4078 3.4132 40.9198 3.23187C41.4318 3.05054 42.0238 2.95987 42.6958 2.95987C43.4211 2.95987 44.0238 3.06654 44.5038 3.27987C44.9944 3.48254 45.3624 3.77587 45.6078 4.15987C45.8531 4.54387 45.9758 5.00787 45.9758 5.55187V9.82387C45.9758 10.0265 46.0238 10.1705 46.1198 10.2559C46.2264 10.3412 46.3491 10.3839 46.4878 10.3839H47.0638V11.5039C46.9358 11.5572 46.7758 11.6105 46.5838 11.6639C46.3918 11.7172 46.1678 11.7439 45.9118 11.7439C45.6238 11.7439 45.3784 11.6905 45.1758 11.5839C44.9838 11.4879 44.8238 11.3492 44.6958 11.1679C44.5784 10.9759 44.4984 10.7625 44.4558 10.5279H44.3598C44.1678 10.7732 43.9331 10.9919 43.6558 11.1839C43.3891 11.3652 43.0798 11.5039 42.7278 11.5999C42.3758 11.7065 41.9811 11.7599 41.5438 11.7599ZM42.0078 10.3839C42.3384 10.3839 42.6424 10.3359 42.9198 10.2399C43.2078 10.1332 43.4531 9.9892 43.6558 9.80787C43.8584 9.61587 44.0184 9.39187 44.1358 9.13587C44.2531 8.8692 44.3118 8.5812 44.3118 8.27187V7.71187C43.5758 7.71187 42.9304 7.75454 42.3758 7.83987C41.8211 7.9252 41.3891 8.0852 41.0798 8.31987C40.7704 8.54387 40.6158 8.85854 40.6158 9.26387C40.6158 9.5092 40.6691 9.7172 40.7758 9.88787C40.8824 10.0479 41.0424 10.1705 41.2558 10.2559C41.4691 10.3412 41.7198 10.3839 42.0078 10.3839ZM48.2126 11.5679V-0.0001297H49.8766V11.5679H48.2126ZM52.0251 11.5679V-0.0001297H53.6891V11.5679H52.0251Z" fill="#F6F6F6"/>
                  <path d="M0 14.2879H54.7611V14.9919H0V14.2879Z" fill="#F6F6F6"/>
                </svg>
              </span>
            ) : (
              <>
                <Translate textKey={'view_all'} />
                <ArrowIcon className="arrow-icon" />
              </>
            )}
          </div>
        )}
      </div>
      <div className="wrapper" style={{ position: 'relative' }}>
        <Swiper
          key={isRTL ? (count > rowItemCountNumber ? "ltr" : windowSize < 980 ? "rtl" : "rtl") : ""}
          modules={[Navigation]}
          spaceBetween={10}
          slidesPerView={isFavoritesYowTv ? 6 : (isYowTv ? defaultYowTvCount : 5)}
          slidesPerGroup={isFavoritesYowTv ? 6 : (isYowTv ? defaultYowTvCount : 5)}
          navigation={isYowTv ? false : true}
          onSlideChange={onSlideChange}
          watchSlidesProgress
          loop={false}
          onSwiper={handleSwiperInit}
          initialSlide={(isRTL && count > rowItemCountNumber) ? count - 1 : 0}
          className={thumbnailOrientation === `PORTRAIT` ? `items portrait ${rowItemsClass}` : `items landscape ${rowItemsClass}`}
          breakpoints={{
            320: {
              slidesPerView: isYowTv
                ? (data?.type === "TOP_TRENDING" ? 1.3 : (thumbnailOrientation === "PORTRAIT" ? 2.2 : 1.3))
                : (thumbnailOrientation === "PORTRAIT" ? 3 : 2),
              slidesPerGroup: 1,
              spaceBetween: isYowTv ? (data?.type === "TOP_TRENDING" ? 30 : 12) : 20,
            },
            480: {
              slidesPerView: isYowTv
                ? (data?.type === "TOP_TRENDING" ? 1.6 : (thumbnailOrientation === "PORTRAIT" ? 2.6 : 1.6))
                : (data?.type == 'FASTCHANNELS' ? 3 : 2),
              slidesPerGroup: 1,
              spaceBetween: isYowTv ? (data?.type === "TOP_TRENDING" ? 36 : 12) : 20,
            },
            768: {
              slidesPerView: isYowTv
                ? (data?.type === "TOP_TRENDING" ? 2.3 : (isContinueWatchingYowTv ? 2.3 : 4))
                : (data?.type == 'FASTCHANNELS' ? 4 : 3),
              slidesPerGroup: isYowTv ? (isYowTv4CardRow ? 1 : 4) : 1,
              spaceBetween: isYowTv ? (data?.type === "TOP_TRENDING" ? 44 : 14) : 20,
            },
            980: {
              slidesPerView: isYowTv4CardRow
                ? 4
                : isFavoritesYowTv
                  ? 6
                  : isYowTv
                    ? (visibleItemsCount ? Math.min(visibleItemsCount, 6) : (thumbnailOrientation === "PORTRAIT" ? 6 : 4))
                    : (data?.type === 'SHORTS' ? 4 : data?.type == 'FASTCHANNELS' ? 9 : effectiveCount ? effectiveCount : 4),
              slidesPerGroup: isYowTv4CardRow
                ? 4
                : isFavoritesYowTv
                  ? 6
                  : isYowTv
                    ? (visibleItemsCount ? Math.min(visibleItemsCount, 6) : (thumbnailOrientation === "PORTRAIT" ? 6 : 4))
                    : (data?.type === 'SHORTS' ? 4 : data?.type == 'FASTCHANNELS' ? 9 : effectiveCount ? Number(effectiveCount) : 4),
              spaceBetween: data?.type === 'SHORTS' ? 10 : (isYowTv && data?.type === "TOP_TRENDING" ? 52 : 20),
            },
            1200: {
              slidesPerView: isYowTv4CardRow
                ? 4
                : isFavoritesYowTv
                  ? 6
                  : isYowTv
                    ? (visibleItemsCount || (thumbnailOrientation === "PORTRAIT" ? 7 : (effectiveCount ?? 5)))
                    : (data?.type === 'SHORTS'
                      ? 5
                      : data?.type == 'FASTCHANNELS'
                        ? 9
                        : projectInfo?.projectConfig?.pubid === 50134
                          ? (thumbnailOrientation === 'PORTRAIT' ? 6 : 4)
                          : (thumbnailOrientation === 'PORTRAIT' ? (effectiveCount ?? portraitFallback) : (effectiveCount ?? landscapeFallback))),
              slidesPerGroup: isYowTv4CardRow
                ? 4
                : isFavoritesYowTv
                  ? 6
                  : isYowTv
                    ? (visibleItemsCount || (thumbnailOrientation === "PORTRAIT" ? 7 : (effectiveCount ?? 5)))
                    : (data?.type === 'SHORTS'
                      ? 5
                      : data?.type == 'FASTCHANNELS'
                        ? 9
                        : projectInfo?.projectConfig?.pubid === 50134
                          ? (thumbnailOrientation === 'PORTRAIT' ? 6 : 4)
                          : (thumbnailOrientation === 'PORTRAIT' ? Number(effectiveCount ?? portraitFallback) : Number(effectiveCount ?? landscapeFallback))),
              spaceBetween: data?.type === 'SHORTS' ? 10 : (isYowTv && data?.type === "TOP_TRENDING" ? 60 : 20),
            },
          }}
          speed={isYowTv ? 450 : 300}
          onSlideChangeTransitionStart={() => {
            if (isYowTv) {
              setIsNavigating(true);
            }
          }}
          onSlideChangeTransitionEnd={(swiper) => {
            if (isYowTv) {
              setCurrentSlideIndex(swiper.activeIndex || 0);
              setIsBeginning(swiper.isBeginning);
              setIsEnd(swiper.isEnd);
              if (swiper?.params?.slidesPerView && typeof swiper.params.slidesPerView === "number") {
                setVisibleSlidesCount(Math.floor(swiper.params.slidesPerView));
              }
              if (navigationTimeoutRef.current) clearTimeout(navigationTimeoutRef.current);
              navigationTimeoutRef.current = setTimeout(() => {
                setIsNavigating(false);
              }, 250);
            }
          }}
          aria-label="Categories Carousel"
        >
          {itemsToRender?.map((item, index) => {
            const currentVisCount = visibleSlidesCount || getVisibleCount();
            const relativeIndex = index - currentSlideIndex;

            // Completely eliminate thumbnail hover distraction during Next and Prev navigation
            const isRightEdgeCard = isYowTv && (
              isControlsHovered ||
              isNavigating ||
              // Left navigation edge: when scrolled, prevent 1st card & any preceding peeking cards from expanding
              (!isBeginning && relativeIndex <= 0) ||
              // Right navigation edge:
              // For 4-card rows (Continue Watching & Top Trending): prevent 5th thumbnail (rel >= 4)
              // For all other category rows: prevent the last visible item (rel === currentVisCount - 1)
              // AND any peeking cards beyond it (rel >= currentVisCount)
              (isYowTv4CardRow
                ? (relativeIndex >= 4 || (currentSlideIndex === 0 && index >= 4))
                : (relativeIndex >= (currentVisCount - 1) || (currentSlideIndex === 0 && index >= (currentVisCount - 1))))
            );

            return (
              <SwiperSlide key={index} className={`${!isYowTv && !isSingleCard && index === activeIndex ? "translate-last" : ""} ${data?.type === "SHORTS" ? "no-transform" : ""}`} aria-label={`Category Slide ${index + 1}`}>
                <div
                  className={data?.type === "SHORTS" ? "shortsCardContainer" : "show"}
                  style={isYowTv ? { width: "100%", maxWidth: "100%", minWidth: 0, boxSizing: "border-box" } : undefined}
                >
                  {data?.type === "MAGAZINES" ? (
                    <MagazineCard
                      data={item}
                      url={data.url}
                      metaData={true}
                      cardHover={true}
                      isPriority={index < rowItemCountNumber}
                    />
                  ) : data?.type === "SHORTS" ? (
                    <ShortsCard
                      data={item}
                      season={false}
                      metaData={true}
                      type={data?.type}
                      vanityUrl={item?.vanity_url}
                      isPriority={index < rowItemCountNumber}
                    />
                  ) : (
                    <ShowCard
                      data={item}
                      season={false}
                      metaData={true}
                      type={data?.type}
                      categoryName={data?.category_name || data?.title}
                      thumbnailOrientation={thumbnailOrientation}
                      displayedData={displayedData}
                      onRemove={handleRemoveItem}
                      rankIndex={data?.type === "TOP_TRENDING" ? index + 1 : undefined}
                      isPriority={index < rowItemCountNumber}
                      disableHover={isRightEdgeCard}
                      disableHoverPreview={isRightEdgeCard}
                      fromFavoritesScreen={fromFavoritesScreen}
                      playlistId={playlistId}
                    />
                  )}
                </div>
              </SwiperSlide>
            );
          })}
        </Swiper>
        {isYowTv && (
          <div className="yow-category-controls">
            <div
              className={`yow-category-nav-zone prev-zone ${isBeginning ? "disabled" : ""}`}
              onMouseEnter={() => setIsControlsHovered(true)}
              onMouseMove={() => setIsControlsHovered(true)}
              onMouseLeave={() => setIsControlsHovered(false)}
              onClick={!isBeginning ? handleSlidePrev : undefined}
            >
              <button
                ref={prevRef}
                className={`yow-category-prev ${isBeginning ? "swiper-button-disabled" : ""}`}
                aria-label="Previous slide"
                disabled={isBeginning}
                onClick={handleSlidePrev}
              >
                <svg width="44" height="44" viewBox="0 0 44 44" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <rect width="44" height="44" fill="#4D4C4B"/>
                  <path d="M24.5 15.75L18.25 22L24.5 28.25" stroke="#F6F6F6" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </button>
            </div>
            <div
              className={`yow-category-nav-zone next-zone ${isEnd ? "disabled" : ""}`}
              onMouseEnter={() => setIsControlsHovered(true)}
              onMouseMove={() => setIsControlsHovered(true)}
              onMouseLeave={() => setIsControlsHovered(false)}
              onClick={!isEnd ? handleSlideNext : undefined}
            >
              <button
                ref={nextRef}
                className={`yow-category-next ${isEnd ? "swiper-button-disabled" : ""}`}
                aria-label="Next slide"
                disabled={isEnd}
                onClick={handleSlideNext}
              >
                <svg width="44" height="44" viewBox="0 0 44 44" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <rect width="44" height="44" fill="#4D4C4B"/>
                  <path d="M19.5 15.75L25.75 22L19.5 28.25" stroke="#F6F6F6" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </button>
            </div>
          </div>
        )}
      </div>
      {(data?.show_all_needed || (isYowTv && data?.shows?.length > 0)) && (
        <div className="view-all-mobile"
          onClick={() => navigateToCategory(navigate, data, thumbnailOrientation)}
          role="button"
          tabIndex={0}
          aria-label={`View all ${data?.category_name}`}
          onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') navigateToCategory(navigate, data, thumbnailOrientation); }}
        >
          {isYowTv ? (
            <span className="yow-view-all-text" aria-label="View all">
              <svg width="55" height="15" viewBox="0 0 55 15" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M4.208 11.5679L0.16 0.59187H2.096L4.816 8.25587C4.86933 8.38387 4.92267 8.5332 4.976 8.70387C5.02933 8.87454 5.08267 9.03987 5.136 9.19987C5.18933 9.3492 5.23733 9.48254 5.28 9.59987H5.36C5.40267 9.48254 5.45067 9.3492 5.504 9.19987C5.55733 9.05054 5.61067 8.89587 5.664 8.73587C5.71733 8.57587 5.77067 8.4212 5.824 8.27187L8.512 0.59187H10.384L6.32 11.5679H4.208ZM11.6189 1.69587V-0.0001297H13.2829V1.69587H11.6189ZM11.6189 11.5679V3.15187H13.2829V11.5679H11.6189ZM18.9194 11.7599C18.0554 11.7599 17.33 11.6052 16.7434 11.2959C16.1674 10.9759 15.73 10.4905 15.4314 9.83987C15.1327 9.1892 14.9834 8.36254 14.9834 7.35987C14.9834 6.34654 15.1327 5.51987 15.4314 4.87987C15.73 4.2292 16.1727 3.7492 16.7594 3.43987C17.346 3.11987 18.0767 2.95987 18.9514 2.95987C19.7514 2.95987 20.418 3.11454 20.9514 3.42387C21.4954 3.72254 21.906 4.1812 22.1834 4.79987C22.4607 5.41854 22.5994 6.1972 22.5994 7.13587V7.77587H16.6954C16.7167 8.3732 16.8074 8.87454 16.9674 9.27987C17.1274 9.67454 17.3674 9.96787 17.6874 10.1599C18.018 10.3412 18.434 10.4319 18.9354 10.4319C19.2447 10.4319 19.522 10.3945 19.7674 10.3199C20.0127 10.2345 20.2207 10.1172 20.3914 9.96787C20.562 9.80787 20.6954 9.6212 20.7914 9.40787C20.8874 9.18387 20.9354 8.9332 20.9354 8.65587H22.5674C22.5674 9.1572 22.482 9.59987 22.3114 9.98387C22.1407 10.3679 21.89 10.6932 21.5594 10.9599C21.2394 11.2159 20.8554 11.4132 20.4074 11.5519C19.9594 11.6905 19.4634 11.7599 18.9194 11.7599ZM16.7274 6.55987H20.8554C20.8554 6.14387 20.8074 5.7972 20.7114 5.51987C20.626 5.23187 20.498 4.9972 20.3274 4.81587C20.1567 4.63454 19.954 4.50654 19.7194 4.43187C19.4847 4.34654 19.218 4.30387 18.9194 4.30387C18.4714 4.30387 18.0874 4.38387 17.7674 4.54387C17.458 4.70387 17.218 4.95454 17.0474 5.29587C16.8767 5.62654 16.77 6.04787 16.7274 6.55987ZM25.8588 11.5679L23.3308 3.15187H25.0588L26.2908 7.75987C26.3548 7.98387 26.4081 8.21854 26.4508 8.46387C26.5041 8.7092 26.5521 8.92787 26.5948 9.11987C26.6374 9.31187 26.6694 9.4452 26.6908 9.51987H26.7868C26.8294 9.32787 26.8774 9.1092 26.9308 8.86387C26.9841 8.61854 27.0321 8.39454 27.0748 8.19187C27.1174 7.97854 27.1494 7.81854 27.1708 7.71187L28.2908 3.15187H30.0828L31.2348 7.72787C31.2774 7.89854 31.3201 8.09587 31.3628 8.31987C31.4161 8.5332 31.4641 8.74654 31.5068 8.95987C31.5601 9.1732 31.5974 9.35987 31.6188 9.51987H31.7148C31.7468 9.37054 31.7841 9.19454 31.8268 8.99187C31.8801 8.77854 31.9334 8.5652 31.9868 8.35187C32.0401 8.13854 32.0881 7.9412 32.1308 7.75987L33.3468 3.15187H34.9308L32.4188 11.5679H30.6908L29.6668 7.37587C29.6241 7.1732 29.5708 6.93854 29.5068 6.67187C29.4428 6.4052 29.3841 6.13854 29.3308 5.87187C29.2774 5.6052 29.2348 5.37054 29.2028 5.16787H29.1068C29.0854 5.2852 29.0534 5.45587 29.0108 5.67987C28.9788 5.90387 28.9308 6.15987 28.8668 6.44787C28.8028 6.73587 28.7281 7.0452 28.6428 7.37587L27.5868 11.5679H25.8588ZM41.5438 11.7599C41.2771 11.7599 40.9891 11.7279 40.6798 11.6639C40.3704 11.5999 40.0771 11.4825 39.7998 11.3119C39.5331 11.1412 39.3144 10.9065 39.1438 10.6079C38.9731 10.2985 38.8878 9.89854 38.8878 9.40787C38.8878 8.83187 39.0211 8.3572 39.2878 7.98387C39.5544 7.61054 39.9278 7.3172 40.4078 7.10387C40.8878 6.89054 41.4584 6.7412 42.1198 6.65587C42.7918 6.57054 43.5224 6.52787 44.3118 6.52787V5.69587C44.3118 5.41854 44.2638 5.1732 44.1678 4.95987C44.0824 4.74654 43.9171 4.5812 43.6718 4.46387C43.4371 4.34654 43.0958 4.28787 42.6478 4.28787C42.1998 4.28787 43.4534 4.34654 41.5918 4.46387C41.3464 4.57054 41.1704 4.7092 41.0638 4.87987C40.9678 5.03987 40.9198 5.21587 40.9198 5.40787V5.64787H39.3198C39.3091 5.59454 39.3038 5.5412 39.3038 5.48787C39.3038 5.43454 39.3038 5.37054 39.3038 5.29587C39.3038 4.79454 44.4424 4.3732 39.7198 4.03187C40.0078 3.67987 40.4078 3.4132 40.9198 3.23187C41.4318 3.05054 42.0238 2.95987 42.6958 2.95987C43.4211 2.95987 44.0238 3.06654 44.5038 3.27987C44.9944 3.48254 45.3624 3.77587 45.6078 4.15987C45.8531 4.54387 45.9758 5.00787 45.9758 5.55187V9.82387C45.9758 10.0265 46.0238 10.1705 46.1198 10.2559C46.2264 10.3412 46.3491 10.3839 46.4878 10.3839H47.0638V11.5039C46.9358 11.5572 46.7758 11.6105 46.5838 11.6639C46.3918 11.7172 46.1678 11.7439 45.9118 11.7439C45.6238 11.7439 45.3784 11.6905 45.1758 11.5839C44.9838 11.4879 44.8238 11.3492 44.6958 11.1679C44.5784 10.9759 44.4984 10.7625 44.4558 10.5279H44.3598C44.1678 10.7732 43.9331 10.9919 43.6558 11.1839C43.3891 11.3652 43.0798 11.5039 42.7278 11.5999C42.3758 11.7065 41.9811 11.7599 41.5438 11.7599ZM42.0078 10.3839C42.3384 10.3839 42.6424 10.3359 42.9198 10.2399C43.2078 10.1332 43.4531 9.9892 43.6558 9.80787C43.8584 9.61587 44.0184 9.39187 44.1358 9.13587C44.2531 8.8692 44.3118 8.5812 44.3118 8.27187V7.71187C43.5758 7.71187 42.9304 7.75454 42.3758 7.83987C41.8211 7.9252 41.3891 8.0852 41.0798 8.31987C40.7704 8.54387 40.6158 8.85854 40.6158 9.26387C40.6158 9.5092 40.6691 9.7172 40.7758 9.88787C40.8824 10.0479 41.0424 10.1705 41.2558 10.2559C41.4691 10.3412 41.7198 10.3839 42.0078 10.3839ZM48.2126 11.5679V-0.0001297H49.8766V11.5679H48.2126ZM52.0251 11.5679V-0.0001297H53.6891V11.5679H52.0251Z" fill="#F6F6F6"/>
                <path d="M0 14.2879H54.7611V14.9919H0V14.2879Z" fill="#F6F6F6"/>
              </svg>
            </span>
          ) : (
            <>
              <Translate textKey={'view_all'} />
              <ArrowIcon className="arrow-icon" />
            </>
          )}
        </div>
      )}
      <ConfirmationPopup
        isOpen={showConfirm}
        onClose={() => setShowConfirm(false)}
        onContinue={handleDeletePlaylist}
        questionText={translateText("delete_from_playlist_placeholder")?.replace("NAME", data?.category_name) || `Are you sure you want to delete ${data?.category_name}?`}
        continueText={translateText("delete") || "Delete"}
        cancelText={translateText("cancel") || "Cancel"}
      />
    </div>
  );
};

export default GaiaCategories;
